import axios from 'axios';

const baseURL = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_BASEURL || 'https://169.58.131.49:8443';
const apiUsername = process.env.REACT_APP_API_USERNAME || 'superadmin';
const apiPassword = process.env.REACT_APP_API_PASSWORD || 'password@123';

export const cleanToken = (rawToken) => {
    if (!rawToken || typeof rawToken !== 'string') return '';
    return rawToken.replace(/^Bearer\s+/i, '').trim();
};

export const isTokenExpired = (token) => {
    if (!token || typeof token !== 'string') return true;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return true;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp) return false;
        // Expire 30 seconds before actual exp timestamp
        return Date.now() >= (payload.exp * 1000 - 30000);
    } catch (e) {
        return false;
    }
};

const envToken = cleanToken(process.env.REACT_APP_API_TOKEN || '');
let storedToken = cleanToken(localStorage.getItem('api_access_token') || localStorage.getItem('token') || '');
if (storedToken && isTokenExpired(storedToken)) {
    localStorage.removeItem('api_access_token');
    localStorage.removeItem('token');
    storedToken = '';
}

let authToken = storedToken || (!isTokenExpired(envToken) ? envToken : '');
let tokenPromise = null;

if (authToken) {
    axios.defaults.headers.common['Authorization'] = authToken;
    axios.defaults.headers.common['accessToken'] = authToken;
    axios.defaults.headers.common['token'] = authToken;
}

// Function to authenticate with login API and get live access token
export const fetchAuthToken = async () => {
    if (tokenPromise) return tokenPromise;
    tokenPromise = (async () => {
        try {
            console.log('Authenticating with /api/Account/login to get fresh accessToken...');
            let res;
            const loginHeaders = {
                'Content-Type': 'application/json'
            };
            try {
                res = await axios.post(`${baseURL}/api/Account/login`, {
                    username: apiUsername,
                    password: apiPassword
                }, {
                    _skipAuth: true,
                    headers: loginHeaders
                });
            } catch (loginErr) {
                console.warn('Attempting fallback to /api/authentication/login...', loginErr);
                res = await axios.post(`${baseURL}/api/authentication/login`, {
                    username: apiUsername,
                    password: apiPassword
                }, {
                    _skipAuth: true,
                    headers: loginHeaders
                });
            }

            const rawToken = res.data?.data?.accessToken
                          || res.data?.data?.token
                          || res.data?.accessToken
                          || res.data?.token
                          || (typeof res.data?.data === 'string' ? res.data?.data : null);

            if (rawToken) {
                authToken = cleanToken(rawToken);
                localStorage.setItem('api_access_token', authToken);
                axios.defaults.headers.common['Authorization'] = authToken;
                axios.defaults.headers.common['accessToken'] = authToken;
                axios.defaults.headers.common['token'] = authToken;
                console.log('Authentication successful. Fresh accessToken set in headers without Bearer.');
                return authToken;
            } else {
                console.error('No accessToken found in /api/Account/login response:', res?.data);
            }
        } catch (err) {
            console.error('Failed to authenticate with login API:', err.response?.data || err.message);
        } finally {
            tokenPromise = null;
        }
        return authToken;
    })();
    return tokenPromise;
};

// Initiate login on app startup if token is missing or expired
if (!authToken || isTokenExpired(authToken)) {
    fetchAuthToken();
}

export const getAuthToken = async () => {
    let token = localStorage.getItem('api_access_token')
             || localStorage.getItem('token')
             || authToken;

    if (token && isTokenExpired(token)) {
        console.warn('Stored token is expired. Refreshing token via /api/Account/login...');
        localStorage.removeItem('api_access_token');
        localStorage.removeItem('token');
        authToken = '';
        token = '';
    }

    if (!token && !isTokenExpired(envToken)) {
        token = envToken;
    }

    if (!token) {
        token = await fetchAuthToken();
    }
    return cleanToken(token);
};

export const getAuthHeaders = () => {
    let token = localStorage.getItem('api_access_token')
             || localStorage.getItem('token')
             || authToken;

    if (token && isTokenExpired(token)) {
        localStorage.removeItem('api_access_token');
        localStorage.removeItem('token');
        authToken = '';
        token = '';
    }

    if (!token && !isTokenExpired(envToken)) {
        token = envToken;
    }

    const tokenVal = cleanToken(token);
    return tokenVal ? {
        Authorization: tokenVal,
        accessToken: tokenVal,
        token: tokenVal
    } : {};
};

// Request interceptor to automatically fetch and attach token to API requests (without Bearer)
axios.interceptors.request.use(async (config) => {
    if (config._skipAuth) {
        if (config.headers) {
            if (typeof config.headers.delete === 'function') {
                config.headers.delete('Authorization');
                config.headers.delete('accessToken');
                config.headers.delete('token');
            } else {
                delete config.headers['Authorization'];
                delete config.headers['accessToken'];
                delete config.headers['token'];
            }
        }
        return config;
    }
    const url = config.url || '';
    if (!url.startsWith('http') || url.startsWith(baseURL)) {
        if (!authToken || isTokenExpired(authToken)) {
            authToken = await fetchAuthToken();
        }
        if (authToken) {
            const tokenVal = cleanToken(authToken);
            config.headers = config.headers || {};
            if (typeof config.headers.set === 'function') {
                config.headers.set('Authorization', tokenVal);
                config.headers.set('accessToken', tokenVal);
                config.headers.set('token', tokenVal);
            } else {
                config.headers['Authorization'] = tokenVal;
                config.headers['accessToken'] = tokenVal;
                config.headers['token'] = tokenVal;
            }
        }
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor to handle expired or unauthorized token (401)
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (
            error.response &&
            error.response.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !originalRequest._skipAuth
        ) {
            originalRequest._retry = true;
            authToken = '';
            localStorage.removeItem('api_access_token');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            delete axios.defaults.headers.common['accessToken'];
            delete axios.defaults.headers.common['token'];

            console.warn('401 Unauthorized received. Re-authenticating via /api/Account/login...');
            const newToken = await fetchAuthToken();
            if (newToken) {
                const tokenVal = cleanToken(newToken);
                if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
                    originalRequest.headers.set('Authorization', tokenVal);
                    originalRequest.headers.set('accessToken', tokenVal);
                    originalRequest.headers.set('token', tokenVal);
                } else {
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers['Authorization'] = tokenVal;
                    originalRequest.headers['accessToken'] = tokenVal;
                    originalRequest.headers['token'] = tokenVal;
                }
                return axios(originalRequest);
            }
        }
        return Promise.reject(error);
    }
);

export default baseURL;